import React, { memo, useRef, useCallback, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { List, useDynamicRowHeight } from 'react-window';
import AutoSizer from 'components/AutoSizer';
import { useIsHorizontalResizeActive } from '@Sage/ssm-web5';
import { subscribe } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import RowComponent from './RowComponent';

import './styles.scss';

const VirtualizedList = memo(
    ({
        entity,
        className,
        total,
        data = [],
        renderRow,
        onLoad,
        overscanCount = 5,
        threshold = 30,
        rowHeight: rowHeightProp = 100,
    }) => {
        const listRef = useRef();
        const isFirstRender = useRef(true);
        const isLoading = useRef(false);
        const isHorizontalResizeActive = useIsHorizontalResizeActive();

        const dynamicRowHeight = useDynamicRowHeight({
            defaultRowHeight: rowHeightProp,
        });

        useEffect(() => {
            if (!isFirstRender.current || !onLoad) return;
            isFirstRender.current = false;
            onLoad({ resetOffset: true });
        }, [onLoad]);

        useEffect(() => {
            if (entity?.entity) {
                return subscribe(`${REFRESH_TABLE}_${entity.entity}`, (lazyInfo) => {
                    isLoading.current = true;
                    Promise.resolve(onLoad({ resetOffset: true, ...(lazyInfo || {}) })).finally(
                        () => {
                            isLoading.current = false;
                        },
                    );
                    listRef.current?.scrollToRow({ index: 0 });
                });
            }
        }, [entity, onLoad]);

        const loadMore = useCallback(() => {
            if (isLoading.current || isHorizontalResizeActive) return;
            isLoading.current = true;
            Promise.resolve(onLoad())
                .catch(() => {
                    // Ignore load errors here; parent handles fetch error states.
                })
                .finally(() => {
                    isLoading.current = false;
                });
        }, [onLoad, isHorizontalResizeActive]);

        // Detect if we need to load more when user scrolls near the end
        const handleRowsRendered = useCallback(
            (visibleRows, allRows) => {
                if (!onLoad || isHorizontalResizeActive) return;

                const { stopIndex } = allRows;
                const dataLength = data.length;
                const totalRows = total || dataLength;

                // If we're near the end and there's more data to load
                if (stopIndex >= dataLength - threshold && dataLength < totalRows) {
                    loadMore();
                }
            },
            [data.length, total, threshold, loadMore, onLoad, isHorizontalResizeActive],
        );

        const finalClassName = classnames('fm-virtualized-list', className);

        const setHeight = useCallback(
            (index, height) => {
                dynamicRowHeight.setRowHeight(index, height);
            },
            [dynamicRowHeight],
        );

        const rowProps = useMemo(
            () => ({
                data,
                setHeight,
                renderRow,
            }),
            [data, setHeight, renderRow],
        );

        const renderList = useCallback(
            (width, height) => {
                return (
                    <div style={{ width, height }}>
                        <List
                            className={finalClassName}
                            rowHeight={dynamicRowHeight}
                            rowCount={data.length}
                            rowProps={rowProps}
                            rowComponent={RowComponent}
                            listRef={listRef}
                            overscanCount={overscanCount}
                        />
                    </div>
                );
            },
            [data.length, rowProps, finalClassName, dynamicRowHeight, overscanCount],
        );

        const renderLazyList = useCallback(
            (width, height) => {
                return (
                    <div style={{ width, height }}>
                        <List
                            className={finalClassName}
                            rowHeight={dynamicRowHeight}
                            rowCount={data.length}
                            rowProps={rowProps}
                            rowComponent={RowComponent}
                            listRef={listRef}
                            onRowsRendered={handleRowsRendered}
                            overscanCount={overscanCount}
                        />
                    </div>
                );
            },
            [
                data.length,
                rowProps,
                finalClassName,
                dynamicRowHeight,
                handleRowsRendered,
                overscanCount,
            ],
        );

        if (!renderRow || !data?.length) return null;

        return (
            <AutoSizer>
                {({ width, height }) => {
                    return (
                        <>
                            {!onLoad && renderList(width, height)}
                            {onLoad && renderLazyList(width, height)}
                        </>
                    );
                }}
            </AutoSizer>
        );
    },
);

export default VirtualizedList;
